import React, { useState } from 'react';
import { History, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { LicenseCard } from './LicenseCard';

/**
 * Gruppiert Lizenzen nach Lizenznummer und zeigt die aktuellste Lizenz
 * (Status-Rang: active > extended > renewed > deactivated, dann valid_until desc)
 * oben an. Vorperioden wandern hinter ein Historie-Toggle — analog zur
 * Admin-Ansicht in InstructorProfilePage.
 *
 * Optional: onDelete(license) — wenn übergeben, erscheint ein Mülleimer-Icon
 * neben jeder Lizenz (nur Admin-Kontext, Caller entscheidet).
 */
export function LicensesGroupedList({ licenses, renewals = [], onDelete = null }) {
    const [showHistory, setShowHistory] = useState({});

    const statusRank = (lic) => {
        if (lic.is_active === false) return 4;
        const s = lic.status || 'active';
        return ({ active: 0, extended: 1, renewed: 2, deactivated: 3 })[s] ?? 5;
    };

    const grouped = {};
    (licenses || []).filter(l => (l.status || 'active') !== 'revoked').forEach(lic => {
        const key = lic.license_number || lic.id;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(lic);
    });
    Object.keys(grouped).forEach(k => {
        grouped[k].sort((a, b) => {
            const ra = statusRank(a), rb = statusRank(b);
            if (ra !== rb) return ra - rb;
            const va = (a.valid_until || '').slice(0, 10);
            const vb = (b.valid_until || '').slice(0, 10);
            if (va !== vb) return vb.localeCompare(va);
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
    });

    const renderCardWithActions = (lic, renewal, isHistory = false) => {
        if (!onDelete) {
            return (
                <div key={lic.id} className={isHistory ? 'opacity-80' : ''}>
                    <LicenseCard license={lic} renewal={renewal} />
                </div>
            );
        }
        return (
            <div key={lic.id} className={`relative group ${isHistory ? 'opacity-80' : ''}`}>
                <LicenseCard license={lic} renewal={renewal} />
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(lic); }}
                    className="absolute top-2 right-2 p-1.5 rounded-md text-rose-600 bg-white/80 hover:bg-rose-50 hover:text-rose-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                    title="Lizenz löschen"
                    data-testid={`grouped-delete-license-${lic.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        );
    };

    return (
        <div className="space-y-3">
            {Object.entries(grouped).map(([num, group]) => {
                const [current, ...history] = group;
                const currentRenewal = (renewals || []).find(r => r.created_license_id === current.id);
                const open = !!showHistory[num];
                return (
                    <div key={num} className="space-y-2">
                        {renderCardWithActions(current, currentRenewal, false)}
                        {history.length > 0 && (
                            <>
                                <button
                                    onClick={() => setShowHistory(s => ({ ...s, [num]: !s[num] }))}
                                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 transition"
                                    data-testid={`portal-toggle-history-${num}`}
                                >
                                    <History className="h-3 w-3" />
                                    {open ? 'Historie ausblenden' : `Historie anzeigen (${history.length})`}
                                    {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>
                                {open && history.map(lic => {
                                    const renewal = (renewals || []).find(r => r.created_license_id === lic.id);
                                    return renderCardWithActions(lic, renewal, true);
                                })}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default LicensesGroupedList;
